/**
 * Activity navigation helpers (TRAV23 activities-front merge).
 *
 * Ported from the activities front's `@utils/hotel`, where they sat alongside the
 * hotel helpers they were forked from. They are activity-specific (they build
 * `Routes.ActivityDetail` / `Routes.ActivityCheckout` URLs), so they live apart from
 * `./hotel` here.
 *
 * They take a react-router `History`, not a `Location`. The activities front passed the
 * result of `useLocation()` into helpers that read `history.location.pathname`, which
 * only worked because its own copy of `getAccountNumberPath` had been changed to read
 * `location.pathname`. RSI's helper reads `history.location.pathname`, so callers here
 * must use `useHistory()`.
 */

import type * as H from 'history';

import { IActivityCompare } from '@common-types';
import type { ISessionKey } from '@share/common-types';
import {
  DATES_LABEL,
  LOCATION_LABEL,
  LOCATION_OBJECT_LABEL,
  ROOMS_LABEL,
  ROOMS_SEARCH_LABEL,
  Routes,
  SESSION_KEY_LABEL,
  SESSION_KEY_LABEL_ROOMS,
} from '@share/constants';
import { getAccountNumberPath, navigateTo, UrlUtils } from '@share/utils';

type AppHistory = H.History<H.LocationState>;

/**
 * Clears the room-level search state that only makes sense inside a hotel booking.
 * Every helper below navigates away from a results context, so none should carry it.
 */
const clearRoomSearchParams = (): void => {
  UrlUtils.removeFromUrl(ROOMS_SEARCH_LABEL);
  UrlUtils.removeFromUrl(SESSION_KEY_LABEL_ROOMS);
};

/**
 * Route to one activity's detail page.
 *
 * The activities front called this `getHotelRoutePath`, shadowing the hotel helper of
 * the same name while actually building `Routes.ActivityDetail`. Renamed so the two
 * cannot be confused now they sit in one codebase.
 */
export const getActivityDetailRoutePath = (
  activityCode: string,
  history: AppHistory,
  sessionKey: ISessionKey | undefined,
): string => {
  const accountPath = getAccountNumberPath(history);
  const encryptedUrl = UrlUtils.getUrl(SESSION_KEY_LABEL, sessionKey);

  return `${accountPath}${Routes.ActivityDetail}/${activityCode}?${encryptedUrl}`;
};

/** Opens an activity's detail page in a new tab (was `openHotelPage` in the activities front). */
export const openActivityDetailPage = (
  activityCode: string,
  history: AppHistory,
  sessionKey: ISessionKey | undefined,
): void => {
  clearRoomSearchParams();
  window.open(getActivityDetailRoutePath(activityCode, history, sessionKey), '_blank');
};

export const getActivityCheckoutRoutePath = (
  activityId: string | undefined,
  rateKey: string | undefined,
  history: AppHistory,
): string => {
  const accountPath = getAccountNumberPath(history);

  return `${accountPath}${Routes.ActivityCheckout}/${activityId}/${rateKey}${window.location.search}`;
};

/**
 * Opens a compared activity's detail page in a new tab, carrying the search context
 * (dates, occupancy, destination, session) that the compare list captured, so the
 * detail page prices the same thing the user was comparing.
 */
export const openActivitySharedPage = (activity: IActivityCompare, history: AppHistory): void => {
  clearRoomSearchParams();

  const accountPath = getAccountNumberPath(history);
  const activityUrl = UrlUtils.getNewUrlWithParams(
    {
      location: {
        pathname: `${accountPath}${Routes.ActivityDetail}/${activity.code}`,
        search: history.location.search,
      },
    },
    [
      { name: DATES_LABEL, data: { startDate: activity.startDate } },
      { name: ROOMS_LABEL, data: activity.roomOccupancies },
      { name: LOCATION_OBJECT_LABEL, data: activity.locationObj },
      { name: LOCATION_LABEL, data: activity.locationObj?.name },
      { name: SESSION_KEY_LABEL, data: activity.sessionKey },
    ],
  );

  window.open(activityUrl, '_blank');
};

export const openActivityCheckoutPage = (
  activityId: string | undefined,
  rateKey: string | undefined,
  history: AppHistory,
): void => {
  clearRoomSearchParams();
  navigateTo(getActivityCheckoutRoutePath(activityId, rateKey, history));
};
